import {
    XyDataSeries,
    HorizontalLineAnnotation,
    ELabelPlacement,
    EAnnotationLayer,
    VerticalLineAnnotation,
    SciChartSurface,
    ZoomPanModifier,
    MouseWheelZoomModifier,
    ZoomExtentsModifier,
    RolloverModifier,
    RubberBandXyZoomModifier,
    EXyDirection,
    EExecuteOn,
    FastImpulseRenderableSeries,
    LegendModifier,
    EAutoRange,
    NumberRange,
    EAxisAlignment,
    NumericAxis,
    DateTimeNumericAxis,
    CursorModifier,
    IRenderableSeries,
    FastLineRenderableSeries,
    EllipsePointMarker,
    AxisMarkerAnnotation,
    BoxAnnotation,
    ECoordinateMode,
    IDataSeries,
    ISciChartSurfaceBase,
    UpdateSuspender,
    EVerticalAnchorPoint,
    EHorizontalAnchorPoint,
    EWrapTo,
    NativeTextAnnotation,
    } from "scichart";
  import { IInitResult, TInitFunction } from "scichart-react";
  import { enMessages } from "../../../../../messages/en-US";
  import { ColumnPaletteProviderPvA, ColumnPaletteProviderPvAUnplanned } from "../PaletteProvider";
  import moment from "moment";
  import { axisLabelDateFormatter } from "../../../../Shared/SciChart/SmartAxis/axisLabelDateFormatter";
  import { GetChartTheme, changeOpacity, getTooltipDataTemplate } from "../../../../Shared/SciChart/ChartConfiguration";
import { LocalizationService } from "@progress/kendo-react-intl";
import { DateTickProvider } from "../../../../Shared/SciChart/SmartAxis/DateTickProvider";
import { SmartDateTZLabelProvider } from "../../../../Shared/SciChart/SmartAxis/SmartDateTZLabelProvider";
import { createDateTzConverter } from "../../../../Shared/SciChart/SmartAxis/timezones";
  
  export interface TComplianceChartConfigResult<TSurface extends ISciChartSurfaceBase> extends IInitResult<TSurface> {
    setAutoRange: (value: boolean) => void,
    showThresholds: (value: boolean) => void,
    changeChartType: (viewType: string, visibleSeriesCompliance: any) => void
  }
  
  export type TComplianceChartConfigFunc = TInitFunction<SciChartSurface, TComplianceChartConfigResult<SciChartSurface>>;

  export interface Props {
    chartData: any
  }

  export interface ChartOptions {
    isAutoRange: boolean;
    viewType: string;
    visibleRange: { min: number | undefined; max: number | undefined; };
    setVisibleRange: (range: any) => void;
    setVisibleSeriesCompliance: (series: any, isChecked: boolean) => void;
    visibleSeriesCompliance: { [key: string]: boolean;};
    isThresholdsOn: boolean;
    isSimpleChart: boolean;
  }
  
  
  export const getChartAPI = (props:Props, chartOptions:ChartOptions, localizationService: LocalizationService, currentLanguage: { locale: string; localeId: string; }) => {
  
    const endFilterDateUnix = moment(props.chartData.dateFilter.endDate).unix();
    const startFilterDateUnix = moment(props.chartData.dateFilter.startDate).unix();
    
    const theme = GetChartTheme();  
    let setAutoRangeCallback: (arg0: boolean) => void;
    let showThresholdsCallback: (arg0: boolean) => void;
    let changeChartTypeCallback: (arg0: string, arg1:any) => void;
  
    const configAfterInit = (initResult) => {
      setAutoRangeCallback = initResult.setAutoRange;
      showThresholdsCallback = initResult.showThresholds;
      changeChartTypeCallback = initResult.changeChartType;
    } 
  
    const setAutoRange = (value: boolean) => {
      if (setAutoRangeCallback !== undefined) {
        setAutoRangeCallback(value);
      }
    }
  
    const showThresholds = (value: boolean) => {
      if (showThresholdsCallback !== undefined) {
        showThresholdsCallback(value);
      }
    }
  
    const changeChartType = (viewType: string, visibleSeriesCompliance: any) => {
      if (changeChartTypeCallback !== undefined) {
        changeChartTypeCallback(viewType, visibleSeriesCompliance);
      }
    }
  
    const initChart : TComplianceChartConfigFunc = async (rootElement: string | HTMLDivElement) => {
  
       const {sciChartSurface, wasmContext} = await SciChartSurface.create(rootElement, {theme: theme, disableAspect: true});
  
       // #region Setup the XAxis
       const xAxis = new DateTimeNumericAxis(wasmContext, { 
        drawMajorBands: false, 
        drawMinorGridLines: false, 
        drawMajorGridLines: false, 
        visibleRange: new NumberRange(chartOptions.visibleRange.min, chartOptions.visibleRange.max),
        visibleRangeLimit: new NumberRange(startFilterDateUnix, endFilterDateUnix),
        growBy: new NumberRange(0.05, 0.05),
        labelStyle: {fontFamily:"Roboto", fontSize:12}
      },);
    
       // Overrides getDeltaFromRange to get nice major and minor deltas for the ticks
       xAxis.deltaCalculator.getDeltaFromRange = axisLabelDateFormatter.getDeltaFromRange;
      
       const updateSettings = (xAxis: NumericAxis) => {
        const timezone = moment.tz.guess();
        // Calculate ticks which respect the time zone
        xAxis.tickProvider = new DateTickProvider(wasmContext, timezone);
        // Draws nice axis labels timezone and locale specific
        xAxis.labelProvider = new SmartDateTZLabelProvider(timezone, true, currentLanguage?.localeId);
        const dateTzConverter = createDateTzConverter(timezone);
        // This is needed for Cursor and Rollover chart modifiers
        xAxis.labelProvider.formatCursorLabel = timestamp =>
            axisLabelDateFormatter.toFullDateTime(timestamp, dateTzConverter, currentLanguage?.localeId);
      };
      // Setting timezone to local system
      updateSettings(xAxis);

      xAxis.visibleRangeChanged.subscribe((args) => { chartOptions.setVisibleRange(args?.visibleRange); });

      sciChartSurface.xAxes.add(xAxis);
      // #endregion
        
      // #region Setup the YAxis
      const yAxis = new NumericAxis(wasmContext,{
        axisAlignment: EAxisAlignment.Right, 
        drawMajorBands: false,
        drawMinorGridLines: false, 
        drawMajorGridLines: true, 
        labelPostfix: " cc", 
        labelPrecision: 2,
        growBy: new NumberRange(0.0, 0.2),
        labelStyle: {fontFamily:"Roboto", fontSize:12}
      });

      sciChartSurface.yAxes.add(yAxis);
      
      // #endregion

      // #region Setup Chart Modifiers
      const legend = new LegendModifier({
        id: "ComplianceLegend",
        placementDivId: "legend-compliance",
        showCheckboxes: true,
        showSeriesMarkers: true,
        showLegend: !chartOptions.isSimpleChart,
        isCheckedChangedCallback: (series: IRenderableSeries, isChecked: boolean) => {
          chartOptions.setVisibleSeriesCompliance(series.id, isChecked)  
        }
      });

      sciChartSurface.chartModifiers.add(legend);

      sciChartSurface.chartModifiers.add(
        new CursorModifier({ showAxisLabels: false,  id: "ComplianceModifierGroup" }),
        new ZoomPanModifier({xyDirection: EXyDirection.XDirection}),
        new MouseWheelZoomModifier({xyDirection:EXyDirection.XDirection}),
        new ZoomExtentsModifier({xyDirection:EXyDirection.XyDirection}),
        new RolloverModifier({tooltipDataTemplate: getTooltipDataTemplate, id: "ComplianceRollOver"}),
        new RubberBandXyZoomModifier({ executeOn: EExecuteOn.MouseRightButton }),
      );
      // #endregion

      // #region Create the Data Series 

      const volumeDispensedDataSeries = new XyDataSeries(wasmContext, {
        xValues: props.chartData.requirements.timestamps, 
        yValues: props.chartData.requirements.volumeDispensedData, 
        dataSeriesName: `${localizationService.toLanguageString('custom.volumeDispensed', enMessages.custom.volumeDispensed)}` 
      });

      const unplannedVolumeDispensedDataSeries = new XyDataSeries(wasmContext, {
        xValues: props.chartData.requirements.unplannedEventsTimestamps, 
        yValues: props.chartData.requirements.unplannedEventsVolumes, 
        dataSeriesName: `${localizationService.toLanguageString('custom.unplannedVolumeDispensed', enMessages.custom.unplannedVolumeDispensed)} (cc)` 
      });

      const filterPeriodDataSeries = new XyDataSeries(wasmContext, {
        xValues: [startFilterDateUnix, endFilterDateUnix],
        yValues: [0,0], 
        dataSeriesName: "filterPeriodSeries"
      });
    
      // Create a series that represents the entire filter period so the chart displays the entire range. 
      const filterPeriodSeries = new FastLineRenderableSeries(wasmContext, {
        id: "FilterPeriodSeriesId", 
        isVisible: false,
        dataSeries: filterPeriodDataSeries
      });  
      sciChartSurface.renderableSeries.add(filterPeriodSeries);
      legend.includeSeries(filterPeriodSeries, false);

      const createVolumeDispensedSeries = (viewType: string, isVisible: boolean, dataSeries: IDataSeries) => {
        let volumeDispensed;
        
        switch(viewType) {
            case 'bar':
            case 'userBar':
                volumeDispensed = new FastImpulseRenderableSeries(wasmContext, {
                    stroke: "rgba(50, 53, 67,1)", 
                    fill: "rgba(50, 53, 67,1)", 
                    strokeThickness: props.chartData.strokeThickness,
                    paletteProvider: new ColumnPaletteProviderPvA(),
                    id: "VolumeDispensedId",
                    dataSeries: dataSeries,
                    isVisible: isVisible
                });
                break;
            case 'line':
            case 'userLine':
            default:
                volumeDispensed = new FastLineRenderableSeries(wasmContext, {
                    dataSeries: dataSeries,
                    stroke: "rgba(50, 53, 67,1)", 
                    id: "VolumeDispensedId",
                    isVisible: isVisible,
                    pointMarker: new EllipsePointMarker(wasmContext, {
                        width: 6, 
                        height: 6, 
                        stroke: "rgba(50, 53, 67,1)", 
                        fill: "rgba(50, 53, 67,1)"
                    })
                });
                break;
        }

        volumeDispensed.rolloverModifierProps.tooltipTextColor = 'White';
        volumeDispensed.rolloverModifierProps.showRollover = true;
        volumeDispensed.rolloverModifierProps.markerColor = 'rgba(50, 53, 67,1)';

        return volumeDispensed;
      }
      const volumeDispensed = createVolumeDispensedSeries(chartOptions.viewType, chartOptions.visibleSeriesCompliance.VolumeDispensedId, volumeDispensedDataSeries);
      sciChartSurface.renderableSeries.add(volumeDispensed);
    
   
      const createUnplannedVolumeDispensedSeries = (viewType: string, isVisible: boolean, dataSeries: IDataSeries) => {
        let unplannedVolumeDispensed;
        
        switch(viewType) {
            case 'bar':
            case 'userBar':
                unplannedVolumeDispensed = new FastImpulseRenderableSeries(wasmContext, {
                    stroke: "#a28bc9", 
                    fill: "#a28bc9", 
                    strokeThickness: props.chartData.strokeThickness,
                    paletteProvider: new ColumnPaletteProviderPvAUnplanned(),
                    id: "UnplannedVolumeDispensedId",
                    dataSeries: dataSeries,
                    isVisible: isVisible
                });
                break;
            case 'line':
            case 'userLine':
            default:
                unplannedVolumeDispensed = new FastLineRenderableSeries(wasmContext, {
                    dataSeries: dataSeries,
                    stroke: "#a28bc9", 
                    id: "UnplannedVolumeDispensedId",
                    isVisible: isVisible,
                    pointMarker: new EllipsePointMarker(wasmContext, {
                        width: 6,
                        height: 6,
                        stroke: "#a28bc9",
                        fill: "#a28bc9"
                    })
                });
                break;
        }

        unplannedVolumeDispensed.rolloverModifierProps.tooltipTextColor = 'White';
        unplannedVolumeDispensed.rolloverModifierProps.showRollover = true;
        unplannedVolumeDispensed.rolloverModifierProps.markerColor = '#a28bc';

        return unplannedVolumeDispensed;
      }

      const unplannedVolumeDispensed = createUnplannedVolumeDispensedSeries(chartOptions.viewType, chartOptions.visibleSeriesCompliance.UnplannedVolumeDispensedId, unplannedVolumeDispensedDataSeries);
      sciChartSurface.renderableSeries.add(unplannedVolumeDispensed);

      // #region Last Sync Series
      const lastSyncUtc = props.chartData.lastSyncUtc;
      const lastSyncSeries = new FastImpulseRenderableSeries(wasmContext, {
        stroke: "grey", 
        fill: "grey", 
        strokeThickness: props.chartData.strokeThickness, 
        paletteProvider: new ColumnPaletteProviderPvA(), 
        id: "LastSyncId"
      });
      
      lastSyncSeries.dataSeries = new XyDataSeries(wasmContext, {
        xValues: [lastSyncUtc], 
        yValues: [0], 
        dataSeriesName: `${localizationService.toLanguageString('custom.lastSync', enMessages.custom.lastSync)}` 
      });

      lastSyncSeries.rolloverModifierProps.tooltipTextColor = 'White'
      lastSyncSeries.rolloverModifierProps.showRollover = true
      // #endregion
    
      const volumeRequirementLine = new FastLineRenderableSeries(wasmContext, {
        stroke: "red", 
        strokeThickness:2, 
        strokeDashArray: [6, 4],
        id: `VolumeRequirementId`,
        isVisible: chartOptions.visibleSeriesCompliance.VolumeRequirementId
      });
  
      const annotationStartDate = startFilterDateUnix > props.chartData.requirements.startDate ? startFilterDateUnix : props.chartData.requirements.startDate;
      const annotationEndDate = endFilterDateUnix < props.chartData.requirements.endDate ? endFilterDateUnix : props.chartData.requirements.endDate;
    

      volumeRequirementLine.dataSeries = new XyDataSeries(wasmContext, {
        xValues: [annotationStartDate, annotationEndDate], 
        yValues: [props.chartData.requirements.volumeRequirement, props.chartData.requirements.volumeRequirement], 
        dataSeriesName: `${localizationService.toLanguageString('custom.volumeRequirement', enMessages.custom.volumeRequirement)} (cc)` 
      });
      
      volumeRequirementLine.rolloverModifierProps.showRollover = false
      sciChartSurface.renderableSeries.add(volumeRequirementLine);

      // #endregion
      
      // #region Setup the API's 
      const getMaxYRange = () => {

        const visibleSeries = sciChartSurface.renderableSeries.asArray().filter(x => x.isVisible);

        var minYRange = 0
        var maxYRange = 0;

          visibleSeries.forEach((series) => {
            const dataSeries = series.dataSeries as XyDataSeries;
            const yRange = dataSeries.getWindowedYRange(dataSeries.xRange, true, false);
          
            if (yRange !== undefined) {
              if (yRange.min < minYRange) {
                minYRange = yRange.min;
              }

              if (yRange.max > maxYRange) {
                maxYRange = yRange.max;
              }
            }
          });

          return {min: minYRange, max: maxYRange};
      };

      const setAutoRange = (value: boolean) => {

        const yAxisCurrent = sciChartSurface.yAxes.get(0);
          
        if (yAxisCurrent !== undefined) {
          if (value) {
            yAxisCurrent.autoRange = EAutoRange.Always;
          } else {
            yAxisCurrent.autoRange = EAutoRange.Never;

            const visibleSeries = sciChartSurface.renderableSeries.asArray().filter(x => x.isVisible);

            if (visibleSeries.length === 0) {
              yAxisCurrent.visibleRange = new NumberRange(0, 10 * 1.20);
            } else {

              var minYRange = 0;
              var maxYRange = 0;
              
              visibleSeries.forEach((series) => {
                const dataSeries = series.dataSeries as XyDataSeries;
                const yRange = dataSeries.getWindowedYRange(dataSeries.xRange, true, false);
              
                if (yRange !== undefined) {
                  if (yRange.min < minYRange) {
                    minYRange = yRange.min;
                  }

                  if (yRange.max > maxYRange) {
                    maxYRange = yRange.max;
                  }
                }
              });

              yAxisCurrent.visibleRange = new NumberRange(minYRange, maxYRange * 1.20);
            }
          }
        }
      };

      setAutoRange(chartOptions.isAutoRange);
      
      const changeChartType = (viewType: string) => {
        UpdateSuspender.using(sciChartSurface, () => {
            const index = sciChartSurface.renderableSeries.asArray().findIndex(x => x.id === "VolumeDispensedId");
            const newVolumeDispensedSeries = createVolumeDispensedSeries(viewType, volumeDispensed.isVisible, volumeDispensed.dataSeries);
            sciChartSurface.renderableSeries.set(index, newVolumeDispensedSeries);

            const index2 = sciChartSurface.renderableSeries.asArray().findIndex(x => x.id === "UnplannedVolumeDispensedId");
            const newUnplannedVolumeDispensedSeries = createUnplannedVolumeDispensedSeries(viewType, unplannedVolumeDispensed.isVisible, unplannedVolumeDispensed.dataSeries);
            sciChartSurface.renderableSeries.set(index2, newUnplannedVolumeDispensedSeries);
        });
    }
  
      const showThresholds = (value: boolean) => {
        UpdateSuspender.using(sciChartSurface, () => {
          sciChartSurface.annotations.asArray().forEach(annotation => {
            if (annotation.id.includes("Threshold")) {
              annotation.isHidden = !value;
            }
          })
      });
      }
      // #endregion

      // #region Setup Annotations
      // Add 0 line annotation
      const horizontalLine = new HorizontalLineAnnotation({
        labelPlacement: ELabelPlacement.Axis,
        stroke: 'grey',
        strokeThickness: 3,
        y1: 0,
        annotationLayer: EAnnotationLayer.BelowChart
        });
        sciChartSurface.annotations.add(horizontalLine)
    

      // Add LastSync line annotation
      const lastSyncLine = new VerticalLineAnnotation({
        labelPlacement: ELabelPlacement.TopRight,
        stroke: 'grey',
        strokeThickness: 3,
        strokeDashArray: [12,5],
        x1: lastSyncUtc,
        showLabel: true,
        labelValue: `${localizationService.toLanguageString('custom.lastSync', enMessages.custom.lastSync)}`,
        axisLabelFill: 'grey',
        annotationLayer: EAnnotationLayer.AboveChart
      });

      // Add Window annotation - complianceWindowInMinutes before last sync to last sync
      const windowStartTime = lastSyncUtc - (props.chartData.complianceWindowInMinutes * 60); 
      const windowTimeinHours = props.chartData.complianceWindowInMinutes / 60;

      function addWindowAnnotation() {
        const visibleYRange = yAxis.visibleRange;
        const topY = visibleYRange.max;

        // Remove all existing window-related annotations
        const existingAnnotations = sciChartSurface.annotations.asArray().filter(a => 
          a.id === "WindowAnnotation" || 
          a.id === "WindowLabelAnnotation" || 
          a.id === "textAnnotationBackground"
        );
        existingAnnotations.forEach(annotation => {
          sciChartSurface.annotations.remove(annotation);
        });

        const windowAnnotation = new BoxAnnotation({
          id: `WindowAnnotation`,
          x1: windowStartTime,
          x2: lastSyncUtc,
          // y1: topY - (topY - bottomY) * 0.05,
          y1: 0,
          y2: topY,
          fill: "rgba(128, 128, 128, 0.3)",
          stroke: "rgba(128, 128, 128, 0.4)",
          strokeThickness: 2,
          xCoordinateMode: ECoordinateMode.DataValue,
          yCoordinateMode: ECoordinateMode.DataValue,
          annotationLayer: EAnnotationLayer.AboveChart,
          
          
        });

        sciChartSurface.annotations.add(windowAnnotation);

        // Add a vertical line annotation with label for the window text
        const windowLabelAnnotation = new VerticalLineAnnotation({
          id: "WindowLabelAnnotation",
          x1: windowStartTime,
          stroke: 'transparent', // Make the line invisible
          strokeThickness: 0,
          showLabel: false,
          labelValue: `Compliance Window (${windowTimeinHours}h)`,
          labelPlacement: ELabelPlacement.TopLeft,
          axisLabelFill: 'grey',
          annotationLayer: EAnnotationLayer.AboveChart
          
        });

        sciChartSurface.annotations.add(windowLabelAnnotation);

           const textAnnotation = new NativeTextAnnotation({
           annotationLayer: EAnnotationLayer.AboveChart,
           id: "textAnnotationBackground",
           x1: windowStartTime,
           y1: topY * 0.99,
           textColor: "grey",
           fontSize: 12,
           
           text: `Compliance Window (${windowTimeinHours}h)`,
           horizontalAnchorPoint: EHorizontalAnchorPoint.Right,
           verticalAnchorPoint: EVerticalAnchorPoint.Top,
           wrapTo: EWrapTo.Annotation,
       });
      sciChartSurface.annotations.add(textAnnotation);
      }

      // Descending sort thresholds by value
      props.chartData.requirements.thresholds.sort((a, b) => (a.value > b.value) ? -1 : 1);
  
      for(let i = 0; i < props.chartData.requirements.thresholds.length; i++) {
        let thresholdStart = 0;
        let thresholdEnd = 0;
  
        if (i === 0) {
          thresholdStart = props.chartData.requirements.thresholds[i].value;
          //set max to a higher value to fill the chart
          thresholdEnd = getMaxYRange().max * 2;
        } else  {
          thresholdStart = props.chartData.requirements.thresholds[i].value;
          thresholdEnd = props.chartData.requirements.thresholds[i-1].value;
        }
  
        const boxAnnotation = new BoxAnnotation({
          id: `Threshold${i}`,
          x1: annotationStartDate, x2: annotationEndDate,
          y1: thresholdStart, y2: thresholdEnd,
          fill: changeOpacity(props.chartData.requirements.thresholds[i].colour, 0.4) ,  // add opacity to fill 
          strokeThickness: 0,
          xCoordinateMode: ECoordinateMode.DataValue,
          annotationLayer: EAnnotationLayer.BelowChart,
          isHidden: !chartOptions.isThresholdsOn
        })
        
        sciChartSurface.annotations.add(boxAnnotation);
      }
  
      sciChartSurface.annotations.add(new AxisMarkerAnnotation({y1: props.chartData.requirements.volumeRequirement,fontSize: 12,backgroundColor: "red"}));
    
      // Add Last Sync series and annotation if within date range
      if(props.chartData.lastSyncUtc >= startFilterDateUnix && props.chartData.lastSyncUtc <= endFilterDateUnix) {
        sciChartSurface.renderableSeries.add(lastSyncSeries);
        legend.includeSeries(lastSyncSeries, false);
        sciChartSurface.annotations.add(lastSyncLine)
      }

      // Only plot the compliance window when it's greater than 0
      if (props.chartData.complianceWindowInMinutes > 0) {
        addWindowAnnotation();

        yAxis.visibleRangeChanged.subscribe(() => {
          addWindowAnnotation();
        });
      }
      
       // #endregion

        return { sciChartSurface: sciChartSurface, setAutoRange, showThresholds, changeChartType};
      
    };
  
    return {initChart, configAfterInit, setAutoRange, showThresholds, changeChartType};
  
  }
  